/**
 * IBKR Accounts settings page.
 *
 * Lets the user manage their IBKR brokerage accounts:
 *   - List existing accounts (default highlighted)
 *   - Add a new account via modal
 *   - Edit an existing account
 *   - Set an account as default
 *   - Delete (blocked if any strategies/positions still reference it)
 *
 * Two connection modes are supported:
 *   - linked   : single gateway login serving multiple sub-accounts (UA / family).
 *                All linked accounts share host/port; order routing happens
 *                per-order via the IBKR account_id field.
 *   - separate : dedicated gateway container per account on the trading EC2.
 *                Different ports and credentials. The modal shows a copy-paste
 *                docker run snippet so the user can provision the container
 *                themselves (we never auto-spawn — IBKR T&Cs).
 *
 * Mobile-first: 1-column on small, 2-column on lg.
 * Dark default per project convention.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { tradingService } from '../../services/tradingService';

// ─── tiny utilities ────────────────────────────────────────────────────────
const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString(); } catch { return s; }
};

const Pill = ({ children, color = 'gray' }) => (
  <span className={`inline-block px-1.5 py-0.5 text-[10px] rounded font-mono uppercase tracking-wider
    ${color === 'gold'   ? 'bg-amber-900/30  text-amber-300  border border-amber-700/40'
    : color === 'green'  ? 'bg-green-900/20  text-green-300  border border-green-700/40'
    : color === 'red'    ? 'bg-red-900/20    text-red-300    border border-red-700/40'
    : color === 'blue'   ? 'bg-blue-900/20   text-blue-300   border border-blue-700/40'
    :                      'bg-gray-800/60   text-gray-300   border border-gray-700/40'}`}>
    {children}
  </span>
);

// ─── Modal ─────────────────────────────────────────────────────────────────
const AccountModal = ({ initial, onClose, onSaved, showToast }) => {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    name:                  initial?.name                  || '',
    account_id:            initial?.account_id            || '',
    is_paper:              initial?.is_paper              ?? true,
    is_active:             initial?.is_active             ?? true,
    connection_mode:       initial?.connection_mode       || 'linked',
    gateway_host:          initial?.gateway_host          || '172.31.32.220',
    gateway_port:          initial?.gateway_port          || 14002,
    client_id_base:        initial?.client_id_base        || 10,
    credentials_env_prefix: initial?.credentials_env_prefix || '',
    notes:                 initial?.notes                 || '',
  });
  const [saving, setSaving]   = useState(false);
  const [err, setErr]         = useState('');

  const onChange = (field) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked
            : e.target.type === 'number'   ? Number(e.target.value)
            : e.target.value;
    setForm({ ...form, [field]: v });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr('');
    try {
      if (isEdit) {
        await tradingService.updateIBKRAccount(initial.id, form);
        showToast(`Updated ${form.name}`);
      } else {
        await tradingService.createIBKRAccount(form);
        showToast(`Created ${form.name}`);
      }
      onSaved();
    } catch (e) {
      const detail = e.response?.data;
      setErr(
        typeof detail === 'string' ? detail :
        detail?.error             ? detail.error :
        detail                    ? JSON.stringify(detail) :
        e.message
      );
    } finally {
      setSaving(false);
    }
  };

  // Snippet to spin up a separate-mode gateway container on the trading EC2.
  // Uses bridge networking (NOT --network host) because the existing
  // ibkr-gateway already grabs port 4002 on the host. Bridge mode maps the
  // container's internal 4002 to whatever ${form.gateway_port} the user
  // chose — no socat proxy needed (the -p mapping replaces it).
  const containerName = `ibkr-${(form.credentials_env_prefix || form.name).toLowerCase().replace(/[^a-z0-9-]/g,'-')}`;
  const envPrefix = form.credentials_env_prefix || 'USER';
  const dockerSnippet =
`# 1. SSH into the trading EC2:
ssh -i KP/maghaweer-trading.pem ec2-user@13.60.38.135

# 2. Add credentials to ~/.env (one-time per separate account):
sudo tee -a ~/.env >/dev/null <<'EOF'
IBKR_${envPrefix}_USER=your_ibkr_username
IBKR_${envPrefix}_PASS=your_ibkr_password
EOF
sudo chmod 600 ~/.env

# 3. Start the gateway container (bridge mode, NOT --network host —
#    the existing container already owns port 4002 on the host):
set -a; source ~/.env; set +a
docker run -d \\
  --name ${containerName} \\
  --restart unless-stopped \\
  -p ${form.gateway_port}:4002 \\
  -e TWS_USERID="$IBKR_${envPrefix}_USER" \\
  -e TWS_PASSWORD="$IBKR_${envPrefix}_PASS" \\
  -e TRADING_MODE=${form.is_paper ? 'paper' : 'live'} \\
  ghcr.io/gnzsnz/ib-gateway:stable

# 4. Wait ~60s for IBC to log in, then apply the UseSSL patch
#    (IBC rewrites jts.ini at login; we need to disable SSL twice):
sleep 60
docker exec ${containerName} sed -i 's/UseSSL=true/UseSSL=false/' /home/ibgateway/Jts/jts.ini
docker exec ${containerName} pkill java
sleep 30
docker exec ${containerName} sed -i 's/UseSSL=true/UseSSL=false/' /home/ibgateway/Jts/jts.ini

# 5. Verify the gateway is reachable on port ${form.gateway_port}:
sleep 30
python3 -c "import socket; s=socket.socket(); s.settimeout(3); s.connect(('127.0.0.1',${form.gateway_port})); print('OK')"`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
         onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-xl bg-white dark:bg-[#0d1117] rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-800">
          <div>
            <h2 className="font-serif text-base text-gray-900 dark:text-gray-100">
              {isEdit ? 'Edit IBKR Account' : 'Add IBKR Account'}
            </h2>
            <p className="text-[11px] text-gray-500 mt-0.5 font-mono">
              {form.connection_mode === 'linked' ? 'Sub-account on shared gateway' : 'Dedicated gateway container'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>

        <form onSubmit={onSubmit} className="px-5 py-4 space-y-3 overflow-y-auto">
          {/* ── Identity ─────────────────────────────────────────────── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Display name" hint="e.g. Paper_Mahroof_1, Live, Mom's IRA">
              <input className={inputClass} value={form.name} onChange={onChange('name')} required maxLength={120}/>
            </Field>
            <Field label="IBKR account #" hint="e.g. DUN309937">
              <input className={inputClass} value={form.account_id} onChange={onChange('account_id')} required maxLength={32}/>
            </Field>
          </div>

          {/* ── Flags ────────────────────────────────────────────────── */}
          <div className="flex gap-4 text-xs">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.is_paper}  onChange={onChange('is_paper')}/>
              <span>Paper account</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.is_active} onChange={onChange('is_active')}/>
              <span>Active</span>
            </label>
          </div>

          {/* ── Connection mode ──────────────────────────────────────── */}
          <Field label="Connection mode">
            <select className={inputClass} value={form.connection_mode} onChange={onChange('connection_mode')}>
              <option value="linked">Linked — share gateway, route per order</option>
              <option value="separate">Separate — dedicated gateway container</option>
            </select>
            <p className="text-[10px] text-gray-500 mt-1">
              {form.connection_mode === 'linked'
                ? 'Use this if multiple IBKR sub-accounts live under one login (UA / family / advisor). Same gateway host+port as your existing account.'
                : 'Use this if this account has its own IBKR credentials. Requires a separate gateway container on the trading EC2.'}
            </p>
          </Field>

          {/* ── Gateway ──────────────────────────────────────────────── */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Gateway host">
              <input className={inputClass} value={form.gateway_host} onChange={onChange('gateway_host')} required/>
            </Field>
            <Field label="Gateway port">
              <input className={inputClass} type="number" value={form.gateway_port} onChange={onChange('gateway_port')} required min={1024} max={65535}/>
            </Field>
            <Field label="Client ID base" hint="multiple of 10">
              <input className={inputClass} type="number" value={form.client_id_base} onChange={onChange('client_id_base')} required min={10} max={9990} step={10}/>
            </Field>
          </div>

          {/* ── Credentials ──────────────────────────────────────────── */}
          <Field label="Credentials env prefix" hint="Blank = legacy IBKR_USER/PASS">
            <input className={inputClass} value={form.credentials_env_prefix} onChange={onChange('credentials_env_prefix')} placeholder="e.g. PAPER_2  (gateway reads IBKR_PAPER_2_USER/PASS)"/>
          </Field>

          {/* ── Separate-mode docker snippet ─────────────────────────── */}
          {form.connection_mode === 'separate' && (
            <div className="bg-amber-900/10 border border-amber-700/30 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wider text-amber-300 mb-1">
                Set up the gateway container manually
              </div>
              <p className="text-[11px] text-amber-200/80 mb-2">
                Django will <em>not</em> auto-spawn this container (IBKR T&amp;Cs).
                Run the snippet below on the trading EC2 first, then save this account.
              </p>
              <pre className="text-[10px] font-mono bg-black/40 text-amber-100 rounded p-2 overflow-x-auto whitespace-pre">
{dockerSnippet}
              </pre>
            </div>
          )}

          {/* ── Notes ────────────────────────────────────────────────── */}
          <Field label="Notes" hint="Free-text, for your own reference">
            <textarea className={`${inputClass} font-mono text-[11px]`} rows={2} value={form.notes} onChange={onChange('notes')}/>
          </Field>

          {/* ── Error / submit ───────────────────────────────────────── */}
          {err && <p className="text-xs text-red-400">{err}</p>}
          <div className="flex justify-end gap-2 pt-2 border-t border-gray-200 dark:border-gray-800">
            <button type="button" onClick={onClose} className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-300">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="px-4 py-1.5 text-xs rounded-lg bg-brand-gold/90 text-black hover:bg-brand-gold disabled:opacity-50">
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create account'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Field wrapper for the modal form ──────────────────────────────────────
const inputClass =
  'w-full px-2.5 py-1.5 text-xs rounded-md bg-gray-100 dark:bg-[#161b22] border border-gray-300 dark:border-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-brand-gold/60';

const Field = ({ label, hint, children }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-wider text-gray-400 mb-1">{label}</label>
    {children}
    {hint && <p className="text-[10px] text-gray-500 mt-0.5">{hint}</p>}
  </div>
);

// ─── Connection status panel (per-account) ────────────────────────────────
// Manages the gateway lifecycle for one account:
//   - Shows current connection state (colored dot + label)
//   - Connect / Disconnect buttons
//   - Polls /connection-status/ while not in a terminal state
//
// Only rendered for separate-mode accounts. Linked-mode accounts use the
// legacy gateway which we don't manage from this UI.
const STATE_STYLES = {
  disconnected:            { dot: 'bg-gray-500',    label: 'Disconnected',          text: 'text-gray-400' },
  connecting:              { dot: 'bg-blue-500',    label: 'Connecting…',            text: 'text-blue-400',   pulse: true },
  awaiting_phone_approval: { dot: 'bg-amber-400',   label: 'Awaiting IBKey tap',     text: 'text-amber-300',  pulse: true },
  awaiting_otp:            { dot: 'bg-amber-400',   label: 'Awaiting 2FA code',      text: 'text-amber-300',  pulse: true },
  connected:               { dot: 'bg-green-500',   label: 'Connected',              text: 'text-green-400' },
  auth_failed:             { dot: 'bg-red-500',     label: 'Auth failed',            text: 'text-red-400' },
  unknown:                 { dot: 'bg-gray-500',    label: 'Unknown',                text: 'text-gray-400' },
};
const TERMINAL_STATES = new Set(['connected', 'auth_failed', 'disconnected']);

const ConnectionPanel = ({ account, toast }) => {
  const [status, setStatus]   = useState({ state: 'unknown', detail: '' });
  const [busy, setBusy]       = useState(false);
  // OTP entry state — only used when status.state is `awaiting_otp` (SMS
  // / email 2FA). Cleared on submit or when we leave the 2FA window.
  const [otp, setOtp]         = useState('');
  const [otpBusy, setOtpBusy] = useState(false);
  const [otpErr, setOtpErr]   = useState('');

  // Poll status on mount + on state change. Faster polling when in
  // transient states (~2s); slower when terminal (~10s) just to detect
  // external changes (e.g. AWS reclaim).
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await tradingService.getIBKRConnectionStatus(account.id);
        if (!cancelled) setStatus(r.data);
      } catch (e) { /* ignore transient */ }
    };
    tick();
    const interval = TERMINAL_STATES.has(status.state) ? 10000 : 2500;
    const t = setInterval(tick, interval);
    return () => { cancelled = true; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account.id, status.state]);

  // Clear the OTP buffer whenever we leave the 2FA-waiting window.
  // (Stale codes are useless — IBKR rotates them on every login attempt.)
  useEffect(() => {
    if (status.state !== 'awaiting_otp' && status.state !== 'awaiting_phone_approval') {
      setOtp('');
      setOtpErr('');
    }
  }, [status.state]);

  const onConnect = async () => {
    setBusy(true);
    try {
      await tradingService.connectIBKRAccount(account.id);
      setStatus({ state: 'connecting', detail: 'Container starting...' });
      toast(`Connecting ${account.name} — watch for the 2FA prompt`);
    } catch (e) {
      window.alert(e.response?.data?.error || e.message);
    } finally { setBusy(false); }
  };

  const onDisconnect = async () => {
    if (!window.confirm(`Disconnect ${account.name}? Active strategies bound to this account will stop firing orders.`)) return;
    setBusy(true);
    try {
      await tradingService.disconnectIBKRAccount(account.id);
      setStatus({ state: 'disconnected', detail: '' });
      toast(`Disconnected ${account.name}`);
    } catch (e) {
      window.alert(e.response?.data?.error || e.message);
    } finally { setBusy(false); }
  };

  const onSubmitOtp = async (e) => {
    e?.preventDefault?.();
    const code = otp.replace(/\D/g, '');
    if (code.length < 4 || code.length > 8) {
      setOtpErr('Enter the 4-8 digit code from your SMS.');
      return;
    }
    setOtpErr('');
    setOtpBusy(true);
    try {
      const r = await tradingService.submitIBKROtp(account.id, code);
      // Optimistically reflect the new state immediately.
      setStatus(r.data);
      setOtp('');
      toast('OTP submitted — finishing login…');
    } catch (e2) {
      setOtpErr(e2.response?.data?.error || e2.message || 'Failed to submit OTP');
    } finally {
      setOtpBusy(false);
    }
  };

  const style = STATE_STYLES[status.state] || STATE_STYLES.unknown;
  const isConnected   = status.state === 'connected';
  const isDisconnected = status.state === 'disconnected';
  const showOtpForm   = status.state === 'awaiting_otp' || status.state === 'awaiting_phone_approval';

  return (
    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-800">
      {/* Status row */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`inline-block w-2 h-2 rounded-full ${style.dot} ${style.pulse ? 'animate-pulse' : ''}`}/>
          <span className={`text-xs font-medium ${style.text}`}>{style.label}</span>
        </div>
        {/* Actions */}
        <div className="flex gap-1.5">
          {!isConnected && (
            <button onClick={onConnect} disabled={busy}
              className="px-2 py-1 text-[11px] rounded border border-green-700/40 text-green-400 hover:bg-green-900/20 disabled:opacity-50">
              {busy ? '…' : 'Connect'}
            </button>
          )}
          {!isDisconnected && (
            <button onClick={onDisconnect} disabled={busy}
              className="px-2 py-1 text-[11px] rounded border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-red-700/40 hover:text-red-400 disabled:opacity-50">
              Disconnect
            </button>
          )}
        </div>
      </div>
      {/* Detail message (state-specific guidance) */}
      {status.detail && (
        <p className={`text-[11px] ${
          status.state === 'auth_failed'    ? 'text-red-400/90'
        : showOtpForm                       ? 'text-amber-300/90'
        : 'text-gray-500'
        }`}>
          {status.detail}
        </p>
      )}
      {/* 2FA panel: shown when the gateway is waiting on Second-Factor auth.
          We surface BOTH paths — IBKey push (tap Approve) and SMS OTP (type
          the code) — because the gateway dialog title is the same for both,
          and which one the user gets depends on their IBKR account settings. */}
      {showOtpForm && (
        <div className="mt-2 p-3 text-[11px] rounded-lg bg-amber-900/15 border border-amber-700/40 text-amber-200 space-y-2">
          <div>
            📱 <strong>Open the IBKR Mobile (IBKey) app:</strong> tap <strong>Approve</strong> if you see a notification.
          </div>
          <div className="text-amber-200/80">
            <strong>Or, if you received an SMS code,</strong> type it below:
          </div>
          <form onSubmit={onSubmitOtp} className="flex gap-2 items-start">
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d{4,8}"
              maxLength={8}
              value={otp}
              onChange={(e) => { setOtp(e.target.value.replace(/\D/g, '').slice(0, 8)); setOtpErr(''); }}
              disabled={otpBusy}
              placeholder="123456"
              className="w-32 px-2 py-1.5 text-sm font-mono tracking-widest text-center rounded bg-black/40 border border-amber-700/50 text-amber-100 placeholder-amber-700/50 focus:outline-none focus:border-amber-500"
              aria-label="One-time code"
            />
            <button
              type="submit"
              disabled={otpBusy || otp.length < 4}
              className="px-3 py-1.5 text-[11px] rounded bg-amber-700/40 border border-amber-600/60 text-amber-100 hover:bg-amber-700/60 disabled:opacity-40 disabled:cursor-not-allowed">
              {otpBusy ? 'Sending…' : 'Submit code'}
            </button>
          </form>
          {otpErr && <div className="text-red-300/90 text-[11px]">{otpErr}</div>}
        </div>
      )}
    </div>
  );
};

// ─── Toast (minimal) ───────────────────────────────────────────────────────
const useToast = () => {
  const [msg, setMsg] = useState('');
  const show = (m) => { setMsg(m); setTimeout(() => setMsg(''), 2400); };
  const node = msg && (
    <div className="fixed bottom-4 right-4 z-50 px-3 py-2 text-xs rounded-md bg-gray-900 text-gray-100 border border-gray-700 shadow-lg">
      {msg}
    </div>
  );
  return [show, node];
};

// ─── Main page ─────────────────────────────────────────────────────────────
const IBKRAccountsPage = () => {
  const [accounts, setAccounts] = useState(null);   // null = loading
  const [err, setErr]           = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [toast, toastNode]      = useToast();

  const refresh = useCallback(async () => {
    try {
      const r = await tradingService.listIBKRAccounts();
      // DRF ModelViewSet without pagination returns a plain array
      const data = Array.isArray(r.data) ? r.data : (r.data?.results || []);
      setAccounts(data);
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const onAdd     = () => { setEditing(null);   setModalOpen(true); };
  const onEdit    = (a) => { setEditing(a);     setModalOpen(true); };
  const onSaved   = () => { setModalOpen(false); refresh(); };

  const onDelete = async (a) => {
    if (!window.confirm(`Delete account "${a.name}"? This cannot be undone.`)) return;
    try {
      await tradingService.deleteIBKRAccount(a.id);
      toast(`Deleted ${a.name}`);
      refresh();
    } catch (e) {
      const detail = e.response?.data;
      window.alert(detail?.error || detail?.detail || e.message);
    }
  };

  const onSetDefault = async (a) => {
    try {
      await tradingService.setDefaultIBKRAccount(a.id);
      toast(`Set ${a.name} as default`);
      refresh();
    } catch (e) {
      window.alert(e.response?.data?.error || e.message);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <h1 className="font-serif text-xl text-gray-900 dark:text-gray-100 tracking-tight">IBKR Accounts</h1>
          <p className="text-[11px] text-gray-500 font-mono uppercase tracking-wider mt-0.5">
            Brokerage accounts available for trading
          </p>
        </div>
        <button onClick={onAdd}
          className="px-3 py-1.5 text-xs rounded-lg bg-brand-gold/90 text-black hover:bg-brand-gold">
          + Add account
        </button>
      </div>

      {err && (
        <div className="mb-4 p-3 text-xs text-red-300 bg-red-900/10 border border-red-700/40 rounded-lg">{err}</div>
      )}

      {/* List */}
      {accounts === null && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-brand-gold border-t-transparent"/>
        </div>
      )}

      {accounts && accounts.length === 0 && (
        <div className="text-center py-12 text-gray-500 text-sm">
          No accounts yet. Add your first IBKR account to start trading.
        </div>
      )}

      {accounts && accounts.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {accounts.map(a => (
            <div key={a.id}
              className={`relative bg-white dark:bg-[#161b22] border rounded-xl p-4
                ${a.is_default ? 'border-amber-700/60' : 'border-gray-200 dark:border-gray-800'}`}>
              {/* Top row: name + pills */}
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-serif text-base text-gray-900 dark:text-gray-100 truncate">{a.name}</h3>
                    {a.is_default  && <Pill color="gold">default</Pill>}
                    {a.is_paper    ? <Pill color="blue">paper</Pill> : <Pill color="red">live</Pill>}
                    {!a.is_active  && <Pill color="gray">inactive</Pill>}
                    <Pill>{a.connection_mode}</Pill>
                  </div>
                  <div className="text-[11px] text-gray-500 mt-0.5 font-mono">
                    IBKR #{a.account_id} · {a.gateway_host}:{a.gateway_port} · client_id={a.client_id_base}..{a.client_id_base+9}
                  </div>
                </div>
              </div>
              {a.notes && <p className="text-[11px] text-gray-500 mt-2 line-clamp-2">{a.notes}</p>}

              {/* Gateway connection panel — only for separate-mode accounts. */}
              {/* Linked-mode uses the legacy gateway which is always-on, no UI control. */}
              {a.connection_mode === 'separate' && (
                <ConnectionPanel account={a} toast={toast} />
              )}

              {/* Actions */}
              <div className="flex flex-wrap gap-1.5 mt-3 pt-2 border-t border-gray-200 dark:border-gray-800">
                <button onClick={() => onEdit(a)}
                  className="px-2 py-1 text-[11px] rounded border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-brand-gold/60 hover:text-brand-gold">
                  Edit
                </button>
                {!a.is_default && (
                  <button onClick={() => onSetDefault(a)}
                    className="px-2 py-1 text-[11px] rounded border border-amber-700/40 text-amber-300 hover:bg-amber-900/20">
                    Set default
                  </button>
                )}
                <button onClick={() => onDelete(a)}
                  className="px-2 py-1 text-[11px] rounded border border-red-700/40 text-red-400 hover:bg-red-900/20">
                  Delete
                </button>
                <span className="ml-auto text-[10px] text-gray-500 font-mono">added {fmtDate(a.created_at)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <AccountModal
          initial={editing}
          onClose={() => setModalOpen(false)}
          onSaved={onSaved}
          showToast={toast}
        />
      )}
      {toastNode}
    </div>
  );
};

export default IBKRAccountsPage;
